import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Euro, Calendar, UserPlus, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, Tooltip, XAxis } from "recharts";
import { api } from "../lib/api";

export default function AdminDashboard() {
  const [ext, setExt] = useState(null);
  const [recentRes, setRecentRes] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/kpis/extended"),
      api.get("/admin/reservations"),
    ])
      .then(([k, r]) => {
        setExt(k.data);
        setRecentRes(r.data.slice(0, 5));
      })
      .catch(() => {});
  }, []);

  const trend = ext?.trend;
  const seriesRevenue = buildSeries(trend, "revenue");
  const seriesRes = buildSeries(trend, "reservations");
  const seriesNew = buildSeries(trend, "new_clients");

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="mb-10">
        <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Dashboard</p>
        <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark">Bonjour Rosalie 👋</h1>
        <p className="text-fegray mt-2">Voici l&apos;état de votre activité aujourd&apos;hui.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="admin-kpis">
        <KpiCard
          icon={Euro}
          label="Chiffre d'affaires"
          series={seriesRevenue}
          format="euro"
          weekVal={fmtEuro(ext?.week?.revenue)}
          weekDelta={ext?.week?.revenue_delta_pct}
          monthVal={fmtEuro(ext?.month?.revenue)}
          monthDelta={ext?.month?.revenue_delta_pct}
          yearVal={fmtEuro(ext?.year?.revenue)}
          yearDelta={ext?.year?.revenue_delta_pct}
          accent
        />
        <KpiCard
          icon={Calendar}
          label="Réservations payées"
          series={seriesRes}
          format="num"
          weekVal={fmtNum(ext?.week?.reservations)}
          weekDelta={ext?.week?.reservations_delta_pct}
          monthVal={fmtNum(ext?.month?.reservations)}
          monthDelta={ext?.month?.reservations_delta_pct}
          yearVal={fmtNum(ext?.year?.reservations)}
          yearDelta={ext?.year?.reservations_delta_pct}
        />
        <KpiCard
          icon={UserPlus}
          label="Nouveaux clients"
          series={seriesNew}
          format="num"
          weekVal={fmtNum(ext?.week?.new_clients)}
          weekDelta={ext?.week?.new_clients_delta_pct}
          monthVal={fmtNum(ext?.month?.new_clients)}
          monthDelta={ext?.month?.new_clients_delta_pct}
          yearVal={fmtNum(ext?.year?.new_clients)}
          yearDelta={ext?.year?.new_clients_delta_pct}
        />
      </div>

      <section className="mt-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-xl font-bold text-fegray-dark">Réservations récentes</h2>
          <Link to="/admin/reservations" className="text-sm text-fegreen font-medium hover:underline">
            Voir tout →
          </Link>
        </div>
        <div className="fe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-fegray-light text-xs uppercase tracking-wider text-fegray border-b border-gray-100">
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Pack</th>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Montant</th>
                <th className="text-left p-4">Statut</th>
              </tr>
            </thead>
            <tbody>
              {recentRes.length === 0 ? (
                <tr><td colSpan={5} className="text-center p-12 text-fegray">Aucune réservation pour le moment</td></tr>
              ) : recentRes.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-fegray-light/50">
                  <td className="p-4">
                    <div className="font-semibold text-fegray-dark">{r.prenom} {r.nom}</div>
                    <div className="text-xs text-fegray">{r.email}</div>
                  </td>
                  <td className="p-4 text-fegray">{r.pack_name}</td>
                  <td className="p-4 text-fegray-dark">{r.date_souhaitee}<div className="text-xs text-fegray">{r.horaire}</div></td>
                  <td className="p-4">
                    <div className="font-bold text-fegreen">{r.amount_after_credit?.toFixed(2)}€</div>
                  </td>
                  <td className="p-4"><StatusBadge status={r.payment_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon: Icon, label, series, format, weekVal, weekDelta, monthVal, monthDelta, yearVal, yearDelta, accent }) {
  const slug = label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const testid = `kpi-${slug}`;
  // Compute trend direction from last 4 weeks (for sparkline color)
  const recent = (series || []).slice(-4);
  const trendUp = recent.length >= 2 && recent[recent.length - 1].value >= recent[0].value;
  const stroke = accent ? "#84C342" : (trendUp ? "#84C342" : "#EF4444");
  const fillId = `grad-${testid}`;
  return (
    <div
      className={`rounded-2xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)] flex flex-col ${
        accent ? "bg-fegray-dark text-white" : "bg-white"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          accent ? "bg-fegreen text-white" : "bg-fegreen-light text-fegreen"
        }`}>
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
          accent ? "text-white/70" : "text-fegray"
        }`}>{label}</span>
      </div>

      {/* Headline: week */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wider font-bold mb-1 text-fegreen">Cette semaine</div>
        <div className="flex items-end justify-between gap-3">
          <div className={`text-3xl font-extrabold leading-none ${accent ? "text-white" : "text-fegray-dark"}`} data-testid={`${testid}-week-val`}>
            {weekVal}
          </div>
          <DeltaPill value={weekDelta} accent={accent} testid={`${testid}-week-delta`} />
        </div>
      </div>

      {/* Sparkline */}
      <div className="-mx-2 h-16 mb-3" data-testid={`${testid}-chart`}>
        {series && series.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 8, bottom: 0, left: 8 }}>
              <defs>
                <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" hide />
              <Tooltip
                cursor={{ stroke: stroke, strokeOpacity: 0.3, strokeDasharray: "3 3" }}
                contentStyle={{
                  background: "#ffffff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  fontSize: "11px",
                  padding: "6px 10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                labelStyle={{ color: "#5C5C5C", fontWeight: 600, marginBottom: "2px" }}
                formatter={(value) => [format === "euro" ? fmtEuro(value) : fmtNum(value), label]}
                separator=" : "
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={stroke}
                strokeWidth={2}
                fill={`url(#${fillId})`}
                isAnimationActive
                animationDuration={600}
                dot={false}
                activeDot={{ r: 3, fill: stroke, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className={`text-[9px] uppercase tracking-wider mb-3 -mt-2 ${accent ? "text-white/50" : "text-fegray/60"}`}>
        Évolution sur 12 semaines
      </div>

      <div className={`border-t pt-3 space-y-2 mt-auto ${accent ? "border-white/10" : "border-gray-100"}`}>
        <SubRow label="Ce mois" val={monthVal} delta={monthDelta} accent={accent} testid={`${testid}-month`} />
        <SubRow label="Cette année" val={yearVal} delta={yearDelta} accent={accent} testid={`${testid}-year`} />
      </div>
    </div>
  );
}

function buildSeries(trend, key) {
  if (!trend || !trend.labels) return [];
  return trend.labels.map((lab, i) => ({
    label: lab,
    value: Number(trend[key]?.[i] || 0),
  }));
}

function SubRow({ label, val, delta, accent, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <div className={`text-xs ${accent ? "text-white/70" : "text-fegray"}`}>{label}</div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-bold ${accent ? "text-white" : "text-fegray-dark"}`}>{val}</span>
        <DeltaPill value={delta} accent={accent} compact />
      </div>
    </div>
  );
}

function DeltaPill({ value, accent, compact, testid }) {
  if (value === null || value === undefined) {
    return (
      <span
        data-testid={testid}
        className={`inline-flex items-center gap-1 ${compact ? "text-[10px]" : "text-[11px]"} font-bold uppercase tracking-wider ${
          accent ? "text-white/50" : "text-fegray/60"
        }`}
      >
        <Minus className="w-3 h-3" /> N-1 —
      </span>
    );
  }
  const up = value > 0;
  const flat = value === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat
    ? (accent ? "text-white/60" : "text-fegray")
    : up
      ? "text-fegreen"
      : "text-red-400";
  const sign = up ? "+" : "";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 ${compact ? "text-[10px]" : "text-[11px]"} font-extrabold uppercase tracking-wider ${cls}`}
      title="Comparaison N-1 (même période, année précédente)"
    >
      <Icon className="w-3 h-3" /> {sign}{value}% N-1
    </span>
  );
}

function fmtEuro(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
}
function fmtNum(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString("fr-FR");
}

export function StatusBadge({ status }) {
  const map = {
    paid: { color: "bg-fegreen-light text-fegreen", label: "Payée" },
    confirmed: { color: "bg-fegreen-light text-fegreen", label: "Confirmée" },
    pending: { color: "bg-yellow-100 text-yellow-700", label: "En attente" },
    pending_payment: { color: "bg-yellow-100 text-yellow-700", label: "Paiement en attente" },
    cancelled: { color: "bg-red-100 text-red-600", label: "Annulée" },
    expired: { color: "bg-gray-100 text-fegray", label: "Expirée" },
  };
  const c = map[status] || { color: "bg-gray-100 text-fegray", label: status };
  return <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-full font-bold ${c.color}`}>{c.label}</span>;
}
